/**
 * Helpers de formatação compartilhados.
 *
 * Princípios:
 *  - Funções puras, sem side effects.
 *  - Sempre tolerantes a null/undefined (devolvem placeholder visual).
 *  - Timezone: navegador (Intl.DateTimeFormat usa locale do user).
 */

// ===== Datas / tempos =====

export function parseDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(value) {
  const d = parseDate(value);
  if (!d) return "—";
  return d.toLocaleString();
}

export function formatRelative(value, now = Date.now()) {
  const d = parseDate(value);
  if (!d) return "—";
  const diff = (now - d.getTime()) / 1000; // segundos
  if (diff < 0) return "agora";
  if (diff < 60) return `${Math.floor(diff)}s atrás`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m atrás`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
  return `${Math.floor(diff / 86400)}d atrás`;
}

export function formatUptime(seconds) {
  if (seconds == null || Number.isNaN(Number(seconds))) return "—";
  const s = Math.floor(Number(seconds));
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

// ===== Status derivado =====

/**
 * Heurística de online/offline:
 *  - online: last_seen <= 2 × heartbeatInterval esperado (default 60s)
 *  - degraded: entre 1x e 5x o esperado
 *  - offline: > 5x ou null
 *
 * Sprint 1 usa um valor fixo, pois não sabemos o heartbeatInterval real
 * do device. 60s é o default do firmware.
 */
const HB_DEFAULT_INTERVAL_S = 60;

export function deviceStatus(device, now = Date.now()) {
  if (!device || !device.last_seen) return "offline";
  const d = parseDate(device.last_seen);
  if (!d) return "offline";
  const ageS = (now - d.getTime()) / 1000;
  if (ageS <= HB_DEFAULT_INTERVAL_S * 2) return "online";
  if (ageS <= HB_DEFAULT_INTERVAL_S * 5) return "degraded";
  return "offline";
}

// ===== RSSI =====

/**
 * Classifica RSSI em qualidade visual.
 *   >= -55 dBm  → excellent
 *   >= -65      → good
 *   >= -75      → fair
 *   <  -75      → poor
 */
export function rssiQuality(rssi) {
  const n = Number(rssi);
  if (rssi == null || Number.isNaN(n)) return "unknown";
  if (n >= -55) return "excellent";
  if (n >= -65) return "good";
  if (n >= -75) return "fair";
  return "poor";
}

export function isRssiBad(rssi) {
  const q = rssiQuality(rssi);
  return q === "fair" || q === "poor";
}

// ===== Heartbeat payload helpers =====

export function pickPayload(device, key, fallback = null) {
  const p = device?.last_hb_payload;
  if (!p || typeof p !== "object") return fallback;
  return p[key] ?? fallback;
}

export function shortDeviceId(deviceId) {
  if (!deviceId) return "—";
  if (deviceId.length <= 16) return deviceId;
  return `${deviceId.slice(0, 8)}…${deviceId.slice(-4)}`;
}

// S7.5C-B — formata bytes em KB com 1 casa decimal. Null/undefined → "—".
// Usado nas colunas de heap (Heap Livre/Mínimo) da DevicesList e do
// FleetHealthCard no DeviceDetail.
export function formatBytesKB(n) {
  if (n == null || typeof n !== "number" || !isFinite(n)) return "—";
  return (n / 1024).toFixed(1) + " KB";
}

// ============================================================================
// S7.6-A — Fleet Inventory: helpers de aging e heap alerts
// ============================================================================
// Não exportam JSX nem classes Tailwind — só níveis textuais. Os
// componentes <OfflineAgingBadge> e <HeapBadge> mapeiam para cores.
// Manter a lógica em format.js facilita testes e reuso fora dos badges.
// HB_DEFAULT_INTERVAL_S já declarado acima (deviceStatus); reusamos.

/**
 * Nível de "aging" baseado em quando o device foi visto pela última vez.
 *
 * @param {string|Date|null} lastSeen — ISO 8601 / Date / null
 * @param {number} now — Date.now() override (para testes)
 * @returns {"online"|"warn"|"alert"|"stale"}
 *   "online" — ≤2 min                     verde
 *   "warn"   — >2 min e ≤1 h (inclui >10 min) amarelo
 *   "alert"  — >1 h e ≤24 h                laranja
 *   "stale"  — >24 h                       cinza
 */
export function offlineAgingLevel(lastSeen, now = Date.now()) {
  const d = parseDate(lastSeen);
  if (!d) return "stale";
  const ageS = (now - d.getTime()) / 1000;
  if (ageS <= HB_DEFAULT_INTERVAL_S * 2) return "online";  // ≤120s
  if (ageS <= 3600) return "warn";                          // ≤1h
  if (ageS <= 86400) return "alert";                        // ≤24h
  return "stale";
}

/**
 * Nível de alerta de heap free.
 *
 * Thresholds aprovados S7.6-A:
 *   <50 KB → "crit"   (vermelho)
 *   <80 KB → "warn"   (amarelo)
 *   ≥80 KB → "ok"     (neutro)
 *   null/inválido → "unknown"
 *
 * @param {number|null|undefined} bytes
 * @returns {"ok"|"warn"|"crit"|"unknown"}
 */
export function heapAlertLevel(bytes) {
  if (bytes == null || typeof bytes !== "number" || !isFinite(bytes)) {
    return "unknown";
  }
  if (bytes < 50 * 1024) return "crit";
  if (bytes < 80 * 1024) return "warn";
  return "ok";
}
